/**
 * <Tabs> — the shadcn tabs, behind an items-array API.
 *
 * shadcn composes Tabs / TabsList / TabsTrigger / TabsContent, which is the
 * right shape when triggers and panels are written out by hand. Every call
 * site here supplies a flat list of id/label/content, so that mapping stays
 * behind this API.
 *
 * The roving-tabindex model the hand-rolled version implemented — arrow keys,
 * Home/End, Space or Enter to activate — comes from radix now rather than
 * being maintained here.
 */
import type { JSX } from 'react';
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  type ReactNode,
} from "react";

import {
  Tabs as TabsRoot,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../ui/tabs.js";

export interface TabItem {
  id: string;
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  defaultId?: string;
  /** Controlled mode. */
  activeId?: string;
  onChange?: (id: string) => void;
  "aria-label"?: string;
}

export interface TabsHandle {
  focus(id: string): void;
}

export const Tabs = forwardRef<TabsHandle, TabsProps>(function Tabs(
  { items, defaultId, activeId, onChange, "aria-label": ariaLabel },
  forwardedRef,
): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => ({
    focus(id: string) {
      // Radix owns the trigger elements, so reach them through the list by
      // their value rather than holding a ref map that would go stale.
      listRef.current
        ?.querySelector<HTMLButtonElement>(`[data-tab-id="${CSS.escape(id)}"]`)
        ?.focus();
    },
  }));

  return (
    <TabsRoot
      value={activeId}
      defaultValue={defaultId ?? items[0]?.id}
      onValueChange={onChange}
    >
      <TabsList ref={listRef} aria-label={ariaLabel}>
        {items.map((item) => (
          <TabsTrigger
            key={item.id}
            value={item.id}
            data-tab-id={item.id}
            disabled={item.disabled}
          >
            {item.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {items.map((item) => (
        <TabsContent key={item.id} value={item.id}>
          {item.content}
        </TabsContent>
      ))}
    </TabsRoot>
  );
});
